import { ChevronRight, ChevronDown, Plus, Search, X } from 'lucide-react'
import { useState, useMemo } from 'react'
import type { FileNode } from '../../types/agentFiles.types'
import { isEssentialFile, isEssentialFolder } from '../../utils/essentialFiles'
import { GitHubFolderIcon } from './GitHubFolderIcon'
import { FileIcon } from '../FileIcon'

interface FileTreeProps {
    files: FileNode[]
    currentFile: string | null
    onFileSelect: (path: string) => void
    onFolderSelect?: (path: string) => void
    onNewFile?: () => void
    onNewFolder?: () => void
    onDeleteFolder?: (folderPath: string) => void
    className?: string
}

export function FileTree({
    files,
    currentFile,
    onFileSelect,
    onFolderSelect,
    onNewFile,
    onNewFolder,
    onDeleteFolder,
    className = ''
}: FileTreeProps) {
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set([]))
    const [searchQuery, setSearchQuery] = useState('')
    const [draggedItem, setDraggedItem] = useState<string | null>(null)

    const toggleFolder = (path: string) => {
        setExpandedFolders(prev => {
            const next = new Set(prev)
            if (next.has(path)) {
                next.delete(path)
            } else {
                next.add(path)
            }
            return next
        })
    }



    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value
        setSearchQuery(query)

        if (query.trim()) {
            const expandAll = (nodes: FileNode[]) => {
                const paths: string[] = []
                nodes.forEach(node => {
                    if (node.type === 'directory') {
                        paths.push(node.path)
                        if (node.children) {
                            paths.push(...expandAll(node.children))
                        }
                    }
                })
                return paths
            }
            setExpandedFolders(new Set(expandAll(files)))
        }
    }

    const handleDragStart = (e: React.DragEvent, path: string) => {
        setDraggedItem(path)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', path)
    }

    const handleDragEnd = () => {
        setDraggedItem(null)
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
    }

    const handleDrop = (e: React.DragEvent, targetPath: string) => {
        e.preventDefault()
        const sourcePath = e.dataTransfer.getData('text/plain')

        if (sourcePath && sourcePath !== targetPath) {
            console.log('Move:', sourcePath, 'to', targetPath)
        }
    }

    // Filter files based on search
    const filteredFiles = useMemo(() => {
        if (!searchQuery.trim()) return files

        const filterNodes = (nodes: FileNode[]): FileNode[] => {
            return nodes.filter(node => {
                const matchesSearch = node.name.toLowerCase().includes(searchQuery.toLowerCase())
                if (node.type === 'directory' && node.children) {
                    const filteredChildren = filterNodes(node.children)
                    return matchesSearch || filteredChildren.length > 0
                }
                return matchesSearch
            }).map(node => {
                if (node.type === 'directory' && node.children) {
                    return {
                        ...node,
                        children: filterNodes(node.children)
                    }
                }
                return node
            })
        }

        return filterNodes(files)
    }, [files, searchQuery])

    const renderNode = (node: FileNode, level: number = 0): React.ReactNode => {
        const isSelected = currentFile === node.path
        const isExpanded = expandedFolders.has(node.path)
        const isDragging = draggedItem === node.path

        if (node.type === 'directory') {
            // Check if folder is essential (cannot be moved/deleted)
            const isEssentialDir = isEssentialFolder(node.path)

            return (
                <div key={node.path}>
                    <button
                        onClick={() => {
                            toggleFolder(node.path)
                            if (onFolderSelect) {
                                onFolderSelect(node.path)
                            }
                        }}
                        onDrop={(e) => handleDrop(e, node.path)}
                        onDragOver={handleDragOver}
                        onContextMenu={(e) => {
                            e.preventDefault()
                            // Only allow delete for non-essential folders
                            if (!isEssentialDir && onDeleteFolder) {
                                onDeleteFolder(node.path)
                            } else if (isEssentialDir) {
                                alert('Esta pasta faz parte da estrutura essencial do agente e não pode ser deletada.')
                            }
                        }}
                        className={`w-full flex items-center gap-1.5 px-2 py-1 text-left transition-colors relative ${isExpanded
                            ? 'bg-gray-100 dark:bg-gray-900'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-900/50'
                            }`}
                        style={{ paddingLeft: `${level * 16 + 8}px` }}
                        title={isEssentialDir ? 'Pasta essencial - não pode ser movida ou deletada' : undefined}
                    >
                        {/* Blue vertical bar when expanded (GitHub style) */}
                        {isExpanded && (
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-700 dark:bg-blue-600 z-10" />
                        )}

                        {isExpanded ? (
                            <ChevronDown className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                        ) : (
                            <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                        )}
                        <GitHubFolderIcon
                            isOpen={isExpanded}
                            className="w-4 h-4 flex-shrink-0 text-blue-500 dark:text-blue-400"
                        />
                        <span className="text-sm flex-1 truncate text-gray-800 dark:text-gray-200">
                            {node.name}
                        </span>
                        {isEssentialDir && (
                            <span className="text-xs text-orange-500 dark:text-orange-400" title="Pasta protegida">🔒</span>
                        )}
                    </button>
                    {isExpanded && node.children && (
                        <div>
                            {node.children.map(child => renderNode(child, level + 1))}
                        </div>
                    )}
                </div>
            )
        }

        // Check if file is essential (cannot be moved/dragged)
        const isEssential = isEssentialFile(node.path)

        return (
            <button
                key={node.path}
                onClick={() => onFileSelect(node.path)}
                draggable={!isEssential}
                onDragStart={isEssential ? undefined : (e) => handleDragStart(e, node.path)}
                onDragEnd={isEssential ? undefined : handleDragEnd}
                className={`w-full flex items-center gap-1.5 px-2 py-1 text-left transition-colors rounded-sm ${isSelected
                    ? 'bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-900/50 text-gray-700 dark:text-gray-300'
                    } ${isDragging ? 'opacity-50' : ''}`}
                style={{ paddingLeft: `${level * 16 + 28}px` }}
                title={node.name}
            >
                <FileIcon name={node.name} className="flex-shrink-0" size={16} />
                <span className="text-sm flex-1 truncate">{node.name}</span>
                {isEssential && (
                    <span className="text-xs text-orange-500 dark:text-orange-400" title="Arquivo protegido">🔒</span>
                )}
                {node.version && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">v{node.version}</span>
                )}
            </button>
        )
    }

    return (
        <div className={`flex flex-col h-full ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                    Files
                </h3>
                <div className="flex items-center gap-1">
                    {onNewFile && (
                        <button
                            onClick={onNewFile}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                            title="New File"
                        >
                            <Plus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                    )}
                    {onNewFolder && (
                        <button
                            onClick={onNewFolder}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                            title="New Folder"
                        >
                            <GitHubFolderIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                    )}
                </div>
            </div>

            {/* Search */}
            <div className="px-2 py-2 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Go to file"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="w-full pl-7 pr-7 py-1 text-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>

            {/* File list */}
            <div className="flex-1 overflow-y-auto px-1 py-1">
                {filteredFiles.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                        {searchQuery ? 'Nenhum arquivo encontrado' : 'Nenhum arquivo'}
                    </div>
                ) : (
                    filteredFiles.map(node => renderNode(node, 0))
                )}
            </div>
        </div>
    )
}
